// Shared utility for creating map popup HTML content

export const createPopupHtml = (listing, onPhotoClick, viewType = 'normal') => {
  // Extract essential data with proper fallbacks
  const photoUrl = listing.photo || '';
  const price = listing.price ? `$${listing.price.toLocaleString()}` : 'Price unavailable';
  const beds = listing.beds || '--';
  const baths = listing.baths || '--';
  const sqft = listing.sqft ? listing.sqft.toLocaleString() : '--';
  const status = listing.status || 'Status';
  const lotSizeAcres = listing.lotSizeAcres || '--';
  const propertyType = listing.propertyType || '';
  
  // Determine if this is a lot or land listing
  const isLotOrLand = propertyType.toLowerCase().includes('lot') || propertyType.toLowerCase().includes('land');
  
  // Process address correctly
  const unparsed = listing.unparsedAddress || '';
  const streetAddress = (() => {
    // Try to extract from unparsed address first
    if (unparsed && listing.city) {
      const idx = unparsed.indexOf(listing.city);
      if (idx > 0) {
        return unparsed.substring(0, idx).trim().replace(/,$/, '');
      }
    }
    
    // Fallback: build from raw fields if needed
    if (listing.raw) {
      const rn = listing.raw;
      return [rn.StreetNumber, rn.StreetDirPrefix, rn.StreetName, rn.StreetSuffix, rn.UnitNumber]
        .filter(Boolean)
        .join(' ')
        .replace(/  +/g, ' ')
        .trim();
    }
    
    return unparsed || 'Address unavailable';
  })();
  
  // Format city, state, zip
  const cityStateZip = (() => {
    const city = listing.city || listing.raw?.City || '';
    const state = listing.state || listing.raw?.StateOrProvince || 'OR';
    const zip = listing.postalCode || listing.raw?.PostalCode || '';
    
    return [city, state, zip].filter(Boolean).join(', ').replace(/, ([0-9]{5})/, ' $1');
  })();
  
  // Get neighborhood or subdivision if available
  let neighborhood = '';
  if (listing.raw?.SubdivisionName && listing.raw.SubdivisionName !== 'N/A') {
    neighborhood = listing.raw.SubdivisionName;
  } else if (listing.city) {
    neighborhood = listing.city;
  } else if (listing.raw?.City) {
    neighborhood = listing.raw.City;
  }

  const cameraIconSvg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="white" viewBox="0 0 16 16" style="margin-right: 4px;">
      <path d="M15 12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h1.172a3 3 0 0 0 2.12-.879l.83-.828A1 1 0 0 1 6.827 3h2.344a1 1 0 0 1 .707.293l.828.828A3 3 0 0 0 12.828 5H14a1 1 0 0 1 1 1v6zM2 4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-1.172a2 2 0 0 1-1.414-.586l-.828-.828A2 2 0 0 0 9.172 2H6.828a2 2 0 0 0-1.414.586l-.828-.828A2 2 0 0 1 3.172 4H2z"/>
      <path d="M8 11a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5zm0-4a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z"/>
    </svg>
  `;
  const publicPhotoCount = (listing?.raw?.Media && Array.isArray(listing.raw.Media))
  ? listing.raw.Media.filter(m => m && m.MediaURL && m.Permission && Array.isArray(m.Permission) && m.Permission.includes('Public') && m.MediaCategory === 'Photo').length
  : 0;
  let photoCountBadgeHtml = '';
  if (publicPhotoCount > 0) {
    photoCountBadgeHtml = `
      <div style="position: absolute; bottom: 5px; right: 5px; background-color: rgba(0, 0, 0, 0.6); color: white; padding: 2px 6px; border-radius: 3px; font-size: 11px; display: flex; align-items: center; z-index: 1; line-height: 1;">
        ${cameraIconSvg}
        ${publicPhotoCount}
      </div>
    `;
  }
  
  // Determine styles based on the view type
  const styles = {
    normal: {
      width: '280px',
      borderRadius: '8px',
      padding: '16px',
      imageHeight: '180px',
      statusMargin: '8px',
      statusPadding: '3px 8px',
      statusBorderRadius: '3px',
      statusFontSize: '10px',
      titleSize: '22px',
      priceMargin: '0 0 10px 0',  // Balanced margin
      textSize: '16px',
      detailSize: '14px',
      addressMargin: '0 0 5px 0',   // Balanced margin
      detailGap: '8px',
      iconSize: '16px',
      addressHeight: '46px',        // Adjusted for better spacing
      detailMargin: '4px',
      paddingBottom: '16px',
      lineHeight: '1.4',            // Balanced line height
      addressLineHeight: '1.3'       // Specific line height for address
    },
    split: {
      width: '240px',
      borderRadius: '8px',
      padding: '12px',
      imageHeight: '160px',
      statusMargin: '12px',
      statusPadding: '4px 10px',
      statusBorderRadius: '4px',
      statusFontSize: '11px',
      titleSize: '20px',
      priceMargin: '0 0 8px 0',
      textSize: '14px',
      detailSize: '13px',
      addressMargin: '0 0 2px 0',  // Reduced bottom margin
      detailGap: '8px',
      iconSize: '14px',
      addressHeight: '38px',       // Slightly reduced height
      detailMargin: '4px',
      paddingBottom: '8px',        // Reduced bottom padding
      lineHeight: '1.4',
      neighborhoodMargin: '2px 0 0 0'  // Specific margin for neighborhood section
    }
  }[viewType];

  // Create unique function name for this listing
  const listingId = listing.id || `listing-${Math.random().toString(36).substring(2, 10)}`;
  const functionName = `openPhotoModal_${listingId.replace(/[-.]/g, '_')}`;
  
  // Define the function on the window object to be called from HTML
  window[functionName] = () => {
    onPhotoClick(listing);
  };
  
  // Status badge color based on status
  const statusColor = {
    'Active': '#4CAF50',
    'Pending': '#FFC107',
    'Closed': '#666666',
    'Coming Soon': '#2196F3'
  }[status] || '#666666';
  
  return `
    <div style="
      width: ${styles.width};
      border: 1px solid #e0e0e0;
      border-radius: ${styles.borderRadius};
      overflow: hidden;
      background-color: #fff;
      box-shadow: 0 2px 8px rgba(0,0,0,0.08);
      font-family: 'Montserrat', Arial, system-ui, sans-serif;
    ">
      <div style="position: relative; width: 100%; padding-bottom: 62.5%; margin: 0; background: #f0f0f0; overflow: hidden; border-radius: 8px 8px 0 0;">
        ${photoUrl ? `
          <img 
            src="${photoUrl}" 
            alt="${streetAddress}" 
            style="
              position: absolute;
              width: 100%;
              height: 100%;
              object-fit: cover;
              top: 0;
              left: 0;
              cursor: pointer;
              margin: 0;
              padding: 0;
              border: none;
              display: block;
            "
            onclick="${functionName}()"
          />` : 
          '<div style="position: absolute; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #888; font-size: 14px;">No image available</div>'
        }
        <div style="
          position: absolute;
          top: 12px;
          left: 12px;
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          background-color: ${statusColor};
          color: #fff;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          white-space: nowrap;
          line-height: 1.2;
        ">
          ${status}
        </div>
        ${photoCountBadgeHtml}
      </div>
      
      <div style="padding: ${styles.padding};">
        <div style="
          font-size: ${styles.titleSize};
          font-weight: 600;
          color: #333;
          margin: 0 0 8px 0;
          padding: 0;
          font-variant-numeric: tabular-nums;
          text-align: left;
          width: 100%;
          box-sizing: border-box;
        ">
          ${price}
        </div>
        
        <div style="
          display: flex;
          align-items: center;
          color: #444;
          margin-bottom: ${viewType === 'split' ? '8px' : '12px'};
          height: 24px;
        ">
          ${isLotOrLand ? `
            <div style="display: flex; align-items: center; font-size: ${styles.detailSize}; color: #444;">
              ${lotSizeAcres !== '--' ? `<span><span style="font-weight: 600;">${lotSizeAcres}</span> acres</span>` : ''}
            </div>
          ` : `
            <div style="display: flex; align-items: center; font-size: ${styles.detailSize}; gap: ${styles.detailMargin};">
              <span style="display: flex; align-items: center;">
                <span style="font-weight: 600;">${beds}</span><span style="margin-left: 2px;">bed</span>
              </span>
              <span style="color: #555; display: flex; align-items: center;">·</span>
              <span style="display: flex; align-items: center;">
                <span style="font-weight: 600;">${baths}</span><span style="margin-left: 2px;">bath</span>
              </span>
              <span style="color: #555; display: flex; align-items: center;">·</span>
              <span style="display: flex; align-items: center;">
                <span style="font-weight: 600;">${sqft}</span><span style="margin-left: 2px;">sqft</span>
              </span>
            </div>
          `}
        </div>
        
        <div style="margin-bottom: 8px; min-height: ${styles.addressHeight};">
          <div style="
            font-size: ${styles.textSize};
            font-weight: 500;
            color: #333;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            margin-bottom: 2px;
            line-height: ${styles.addressLineHeight || '1.2'};
            padding-right: 2px;
          " title="${streetAddress}">
            ${streetAddress}
          </div>
          <div style="
            font-size: ${styles.detailSize};
            color: #666;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            line-height: 1.2;
            padding-right: 2px;
          ">
            ${cityStateZip}
          </div>
        </div>
        
        ${(viewType === 'split' && listing.listingType) ? `
          <div style="font-size: ${styles.detailSize}; color: #888; margin-bottom: 2px;">
            ${listing.listingType}
          </div>
        ` : ''}
        ${(viewType === 'split' && listing.yearSold) ? `
          <div style="font-size: ${styles.detailSize}; color: #444; margin-bottom: 2px;">
            <span style="font-weight: 600;">${listing.yearSold}</span>
          </div>
        ` : ''}
        ${neighborhood ? `
          <div style="
            font-size: ${styles.detailSize};
            color: #555;
            display: flex;
            align-items: center;
            height: 20px;
            margin: ${styles.neighborhoodMargin || '2px 0 0 0'};
          ">
            <svg width="${styles.iconSize}" height="${styles.iconSize}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="margin-right: 4px; flex-shrink: 0;">
              <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" fill="#555"/>
            </svg>
            <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${neighborhood}</span>
          </div>
        ` : ''}
      </div>
    </div>
  `;
};
